import React from 'react';
import Card from '@mui/material/Card';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import Icon from '@mui/material/Icon';
import Grid from '@mui/material/Grid';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { useTheme as useMuiTheme, alpha } from '@mui/material/styles';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';

import ExpensesList from './ExpensesList';
import AddExpenseModal from './modal/AddExpenseModal';
import MarkExpensePaidModal from './modal/MarkExpensePaidModal';
import MarkExpensePendingModal from './modal/MarkExpensePendingModal';
import { trMoney } from '../../utils/accountingFormat';

function SectionCard({
  title,
  subtitle,
  count,
  total,
  children,
  onDragOver,
  onDragLeave,
  onDrop,
  isActiveDrop,
}) {
  return (
    <Card
      sx={{
        borderRadius: '18px',
        border: isActiveDrop ? '1px solid rgba(76,175,80,0.45)' : '1px solid rgba(0,0,0,0.08)',
        boxShadow: 'none',
        overflow: 'hidden',
        height: '100%',
        background: isActiveDrop ? 'rgba(76,175,80,0.05)' : undefined,
        transition: 'all 160ms ease',
      }}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      <MDBox
        sx={{
          px: 1.25,
          py: 1.1,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 1,
          flexWrap: 'wrap',
        }}
      >
        <MDBox sx={{ minWidth: 0 }}>
          <MDTypography variant="button" fontWeight="bold">
            {title}
          </MDTypography>
          <MDTypography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
            {subtitle}
          </MDTypography>
        </MDBox>

        <MDBox sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
          <Chip
            size="small"
            label={`${count} kayıt`}
            sx={{ borderRadius: '999px', fontWeight: 800 }}
          />
          <Chip
            size="small"
            label={trMoney(total)}
            sx={{ borderRadius: '999px', fontWeight: 900 }}
          />
        </MDBox>
      </MDBox>

      <Divider />

      <MDBox sx={{ p: 1.25 }}>{children}</MDBox>
    </Card>
  );
}

function getExpenseAmount(row) {
  return Number(row?.amountTry ?? 0);
}

export default function AccountingExpensesCard({
  rows = [],
  title = 'Giderler',
  subtitle = 'Operasyon, sabit gider ve diger gider kalemleri',
  onCreateExpense,
  onMarkExpensePaid,
  onMarkExpensePending,
  onDeleteExpense,
}) {
  const theme = useMuiTheme();
  const isDark = theme.palette.mode === 'dark';

  const [draggedExpense, setDraggedExpense] = React.useState(null);
  const [dropTargetMode, setDropTargetMode] = React.useState(null);

  const [openAddExpense, setOpenAddExpense] = React.useState(false);
  const [selectedExpense, setSelectedExpense] = React.useState(null);

  const [openPaidModal, setOpenPaidModal] = React.useState(false);
  const [openPendingModal, setOpenPendingModal] = React.useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = React.useState(false);

  const [actionSubmitting, setActionSubmitting] = React.useState(false);

  const pendingRows = React.useMemo(() => rows.filter((item) => item.status === 'PENDING'), [rows]);

  const paidRows = React.useMemo(() => rows.filter((item) => item.status === 'PAID'), [rows]);

  const total = React.useMemo(
    () => rows.reduce((sum, item) => sum + getExpenseAmount(item), 0),
    [rows]
  );

  const totalPending = React.useMemo(
    () => pendingRows.reduce((sum, item) => sum + getExpenseAmount(item), 0),
    [pendingRows]
  );

  const totalPaid = React.useMemo(
    () => paidRows.reduce((sum, item) => sum + getExpenseAmount(item), 0),
    [paidRows]
  );

  const softBtnSx = React.useMemo(
    () => ({
      height: 34,
      borderRadius: '999px',
      textTransform: 'none',
      fontWeight: 800,
      px: 1.4,
      minWidth: 'auto',
      border: `1px solid ${alpha(theme.palette.success.main, isDark ? 0.28 : 0.2)}`,
      background: alpha(theme.palette.success.main, isDark ? 0.14 : 0.08),
      color: theme.palette.success.main,
      boxShadow: 'none',
      '&:hover': {
        background: alpha(theme.palette.success.main, isDark ? 0.2 : 0.12),
        boxShadow: 'none',
      },
    }),
    [theme.palette.success.main, isDark]
  );

  const resetActionState = React.useCallback(() => {
    if (actionSubmitting) return;
    setSelectedExpense(null);
    setOpenPaidModal(false);
    setOpenPendingModal(false);
    setOpenDeleteDialog(false);
  }, [actionSubmitting]);

  const handleCreateExpense = async (payload) => {
    await onCreateExpense?.(payload);
    setOpenAddExpense(false);
  };

  const handleOpenMoveToPaid = React.useCallback((expense) => {
    setSelectedExpense(expense);
    setOpenPaidModal(true);
  }, []);

  const handleOpenMoveToPending = React.useCallback((expense) => {
    setSelectedExpense(expense);
    setOpenPendingModal(true);
  }, []);

  const handleOpenDelete = React.useCallback((expense) => {
    setSelectedExpense(expense);
    setOpenDeleteDialog(true);
  }, []);

  const handleExpenseDrop = React.useCallback(
    (targetMode) => {
      if (!draggedExpense) return;

      const currentMode = draggedExpense.status === 'PAID' ? 'PAID' : 'PENDING';

      if (currentMode === targetMode) {
        setDraggedExpense(null);
        setDropTargetMode(null);
        return;
      }

      if (targetMode === 'PAID') {
        handleOpenMoveToPaid(draggedExpense);
      } else {
        handleOpenMoveToPending(draggedExpense);
      }

      setDraggedExpense(null);
      setDropTargetMode(null);
    },
    [draggedExpense, handleOpenMoveToPaid, handleOpenMoveToPending]
  );

  const handleSubmitPaid = async (payload) => {
    if (!selectedExpense) return;
    try {
      setActionSubmitting(true);
      await onMarkExpensePaid?.(selectedExpense, payload);
      resetActionState();
    } finally {
      setActionSubmitting(false);
    }
  };

  const handleSubmitPending = async (payload) => {
    if (!selectedExpense) return;
    try {
      setActionSubmitting(true);
      await onMarkExpensePending?.(selectedExpense, payload);
      resetActionState();
    } finally {
      setActionSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedExpense) return;
    try {
      setActionSubmitting(true);
      await onDeleteExpense?.(selectedExpense);
      resetActionState();
    } finally {
      setActionSubmitting(false);
    }
  };

  return (
    <>
      <Card
        sx={{
          borderRadius: '18px',
          border: `1px solid ${theme.palette.divider}`,
          overflow: 'hidden',
          boxShadow: 'none',
        }}
      >
        <MDBox
          sx={{
            px: 1.25,
            pt: 1.1,
            pb: 1.1,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 1,
            flexWrap: 'wrap',
          }}
        >
          <MDBox sx={{ minWidth: 0 }}>
            <MDTypography variant="button" fontWeight="bold">
              {title}
            </MDTypography>
            <MDTypography variant="caption" sx={{ color: theme.palette.text.secondary }}>
              {subtitle}
            </MDTypography>
          </MDBox>

          <MDBox
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              flexShrink: 0,
              flexWrap: 'wrap',
            }}
          >
            <Chip
              size="small"
              label={`Toplam ${trMoney(total)}`}
              sx={{ borderRadius: '999px', fontWeight: 900 }}
            />

            <Button
              size="small"
              startIcon={<Icon fontSize="small">add</Icon>}
              sx={softBtnSx}
              onClick={() => setOpenAddExpense(true)}
            >
              Gider ekle
            </Button>
          </MDBox>
        </MDBox>

        <Divider />

        <MDBox sx={{ p: 1.25 }}>
          <Grid container spacing={1.5}>
            <Grid item xs={12} md={6}>
              <SectionCard
                title="Bekleyen Giderler"
                subtitle="Vadesi gelen ve odemesi bekleyen kayitlar"
                count={pendingRows.length}
                total={totalPending}
                isActiveDrop={dropTargetMode === 'PENDING'}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDropTargetMode('PENDING');
                }}
                onDragLeave={() => {
                  setDropTargetMode((prev) => (prev === 'PENDING' ? null : prev));
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  handleExpenseDrop('PENDING');
                }}
              >
                <ExpensesList
                  rows={pendingRows}
                  mode="PENDING"
                  onMarkPaid={handleOpenMoveToPaid}
                  onMoveToPending={null}
                  onDelete={handleOpenDelete}
                  onDragStart={setDraggedExpense}
                  onDragEnd={() => {
                    setDraggedExpense(null);
                    setDropTargetMode(null);
                  }}
                />
              </SectionCard>
            </Grid>

            <Grid item xs={12} md={6}>
              <SectionCard
                title="Yapılan Ödemeler"
                subtitle="Odeme kaydi tamamlanan gider hareketleri"
                count={paidRows.length}
                total={totalPaid}
                isActiveDrop={dropTargetMode === 'PAID'}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDropTargetMode('PAID');
                }}
                onDragLeave={() => {
                  setDropTargetMode((prev) => (prev === 'PAID' ? null : prev));
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  handleExpenseDrop('PAID');
                }}
              >
                <ExpensesList
                  rows={paidRows}
                  mode="PAID"
                  onMarkPaid={null}
                  onMoveToPending={handleOpenMoveToPending}
                  onDelete={handleOpenDelete}
                  onDragStart={setDraggedExpense}
                  onDragEnd={() => {
                    setDraggedExpense(null);
                    setDropTargetMode(null);
                  }}
                />
              </SectionCard>
            </Grid>
          </Grid>
        </MDBox>
      </Card>

      <AddExpenseModal
        open={openAddExpense}
        onClose={() => setOpenAddExpense(false)}
        onSubmit={handleCreateExpense}
      />

      <MarkExpensePaidModal
        open={openPaidModal}
        expense={selectedExpense}
        onClose={resetActionState}
        onSubmit={handleSubmitPaid}
      />

      <MarkExpensePendingModal
        open={openPendingModal}
        expense={selectedExpense}
        onClose={resetActionState}
        onSubmit={handleSubmitPending}
      />

      <Dialog open={openDeleteDialog} onClose={resetActionState} fullWidth maxWidth="xs">
        <DialogTitle>Gideri sil</DialogTitle>
        <DialogContent>
          <MDTypography variant="button" sx={{ color: 'text.secondary' }}>
            &quot;{selectedExpense?.title || 'Bu gider'}&quot; kaydını silmek istiyor musun? Bu
            işlem geri alınamaz.
          </MDTypography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={resetActionState} disabled={actionSubmitting}>
            Vazgeç
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={actionSubmitting}
          >
            Sil
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
